/**
 * Detects that a newer admin bundle has been deployed while this tab was
 * open (2026-08-02). Root cause of a live incident: an SPA tab left open
 * never refetches index.html, so `no-cache` on it only protects fresh
 * loads — the client's phone ran a days-old bundle and its evening
 * timezone bug "came back" hours after it was fixed on the server.
 *
 * Mechanism: index.html references its one hashed entry script
 * (assets/index-<hash>.js). Refetching index.html with cache disabled and
 * comparing that hash against the script this tab actually loaded tells
 * us, with zero server support, whether a new build was deployed.
 */

const ENTRY_RE = /assets\/index-([A-Za-z0-9_-]+)\.js/;

export function entryHashOf(html: string): string | null {
  return ENTRY_RE.exec(html)?.[1] ?? null;
}

/** The hash of the entry script THIS tab is running, from its own DOM. */
export function currentEntryHash(doc: Document = document): string | null {
  for (const script of Array.from(doc.querySelectorAll('script[src]'))) {
    const hash = entryHashOf(script.getAttribute('src') ?? '');
    if (hash) return hash;
  }
  return null;
}

export const DEPLOY_CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Calls `onNewDeploy` once when the served bundle stops matching the
 * running one. Checks every five minutes and whenever the tab becomes
 * visible again (the phone-in-pocket case that caused the incident).
 * Returns a stop function. Never throws: a failed check is a skipped
 * check — connectivity noise must not break the app it ships with.
 */
export function watchForNewDeploy(onNewDeploy: () => void): () => void {
  const running = currentEntryHash();
  if (!running) return () => {};
  let stopped = false;
  let fired = false;

  const check = async (): Promise<void> => {
    if (stopped || fired) return;
    try {
      const res = await fetch('/', { cache: 'no-store' });
      if (!res.ok) return;
      const served = entryHashOf(await res.text());
      if (served && served !== running && !fired) {
        fired = true;
        onNewDeploy();
      }
    } catch {
      // Offline or flaky: skip this round.
    }
  };

  const interval = window.setInterval(() => void check(), DEPLOY_CHECK_INTERVAL_MS);
  const onVisible = () => {
    if (document.visibilityState === 'visible') void check();
  };
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    stopped = true;
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
